import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@components/atoms/icon';
import { MiniArrowIcon } from '@components/icons/mini-arrow-icon';
import type { PoaRequirementsPageProps } from '@modules/widget/pages/poa-requirements-page/types';
import { RequirementsPageContainer } from '@modules/widget/pages/styles';
import { getImageUrl } from '@utils/get-image-url';

export const PoaRequirementsPage: FC<PoaRequirementsPageProps> = ({ onBack }) => {
    const { t } = useTranslation();
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

    const cards = useMemo(
        () => [
            {
                title: t('requirementsPage.poa.blurryImageTitle'),
                description: t('requirementsPage.poa.blurryImageDescription'),
                img: getImageUrl('/images/sdk/poa/blurry-image.png'),
            },
            {
                title: t('requirementsPage.poa.incompleteDocumentTitle'),
                description: t('requirementsPage.poa.incompleteDocumentTitleDescription'),
                img: getImageUrl('/images/sdk/poa/incomplete-document.png'),
            },
            {
                title: t('requirementsPage.poa.fadedTextTitle'),
                description: t('requirementsPage.poa.fadedTextDescription'),
                img: getImageUrl('/images/sdk/poa/faded-text.png'),
            },
            {
                title: t('requirementsPage.poa.obstructedInformationTitle'),
                description: t('requirementsPage.poa.obstructedInformationDescription'),
                img: getImageUrl('/images/sdk/poa/obstructed-information.png'),
            },
            {
                title: t('requirementsPage.poa.incorrectAngles'),
                description: t('requirementsPage.poa.incorrectAnglesDescription'),
                img: getImageUrl('/images/sdk/poa/incorrect-angles.png'),
            },
            {
                title: t('requirementsPage.poa.lowContrastTitle'),
                description: t('requirementsPage.poa.lowContrastTitleDescription'),
                img: getImageUrl('/images/sdk/poa/low-contrast.png'),
            },
            {
                title: t('requirementsPage.poa.nonStandardDocumentTitle'),
                description: t('requirementsPage.poa.nonStandardDocumentDescription'),
                img: getImageUrl('/images/sdk/poa/non-standard-document.png'),
            },
            {
                title: t('requirementsPage.poa.outdatedDocumentTitle'),
                description: t('requirementsPage.poa.outdatedDocumentDescription'),
                img: getImageUrl('/images/sdk/poa/outdated-document.png'),
            },
        ],
        [t],
    );

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else {
            window.history.back();
        }
    };

    return (
        <RequirementsPageContainer isUseWebSDK={!!onBack}>
            {searchParams.get('hideHeader') === 'true' ? (
                <React.Fragment />
            ) : (
                <div className='page-header'>
                    <Icon
                        icon={<MiniArrowIcon className='arrow-icon' width={18} height={14} />}
                        $clickable={true}
                        onClick={handleBackClick}
                    />
                    <h2 className='page-title'>{t('requirementsPage.poa.title')}</h2>
                    <div className='page-hidden-elem' />
                </div>
            )}
            <div
                className='page-description'
                dangerouslySetInnerHTML={{ __html: t('requirementsPage.poa.description') }}
            />
            <div className='card-container'>
                {cards.map((card, index) => (
                    <React.Fragment key={card.title}>
                        <div className='card'>
                            <img src={card.img} alt={card.title} className='card-img' />
                            <div className='card-title'>{card.title}</div>
                            <div className='card-description'>{card.description}</div>
                        </div>
                        {index % 2 === 1 && index !== cards.length - 1 && (
                            <div className='divider' />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </RequirementsPageContainer>
    );
};
